import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm, change, formValueSelector } from 'redux-form';
import Toggle from 'react-toggle';
import { withRouter } from 'react-router';
import { ButtonV2 } from '@zola/zola-ui';

import {
  RenderField,
  RenderTextareaField,
  RenderSubmitButton,
  RenderHiddenField,
} from '../../common/form';
import { updatePage } from '../../../actions/website/PageActions';
import * as Validations from '../../common/form/commonValidations';
import { uploadPhoto } from '../../../actions/website/FileUploadActions';
import FileInput from '../../common/form/FileInput';
import { getCmsPageDescriptionPlaceholder } from './entities/CmsPageTypes';
import { updateShowPoiMap } from '../../../actions/WeddingActions';

class CmsPageForm extends Component {
  constructor(props) {
    super(props);

    this.removePhoto = this.removePhoto.bind(this);
    this.routerWillLeave = this.routerWillLeave.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.handleSuccess = this.handleSuccess.bind(this);
    this.updateHeaderImageState = this.updateHeaderImageState.bind(this);

    const { headerImgUrl, filePreview } = this.props; // eslint-disable-line
    this.state = {
      hasHeaderImage: !!(headerImgUrl || filePreview.cloudfront_url),
    };
  }

  componentDidMount() {
    this.props.router.setRouteLeaveHook(
      this.props.routes[this.props.routes.length - 1],
      this.routerWillLeave
    );
  }

  componentDidUpdate(prevProps) {
    const { page, onUpdatePage, onUploadSuccess, filePreview } = this.props;
    if (
      prevProps.filePreview.uuid !== filePreview.uuid &&
      prevProps.page === this.props.page &&
      this.props.filePreview.uuid
    ) {
      onUploadSuccess(this.props.filePreview);
      this.updateHeaderImageState(true);
    } else if (prevProps.page !== this.props.page) {
      this.updateHeaderImageState(!!this.props.headerImgUrl);
    } else if (
      this.props.formValues.header_image_id &&
      prevProps.formValues.header_image_id !== this.props.formValues.header_image_id &&
      page.type === prevProps.formValues.type
    ) {
      // eslint-disable-line react/prop-types
      const newPageValues = { ...page, ...this.props.formValues };
      onUpdatePage(newPageValues);
    }
  }

  onSubmit(values) {
    const { handleSubmit } = this.props;
    const submitReturn = handleSubmit(values);
    submitReturn.then(this.handleSuccess);
  }

  updateHeaderImageState(updatedState) {
    this.setState({
      hasHeaderImage: updatedState,
    });
  }

  routerWillLeave() { //eslint-disable-line
    const { dirty } = this.props;
    if (dirty) {
      return 'You have unsaved changes. Hit "Cancel" to save them or risk losing your work.';
    }
  }

  removePhoto() {
    const { deletePhoto } = this.props;
    this.setState({ hasHeaderImage: false });
    deletePhoto();
    setTimeout(() => {
      this.onSubmit();
    }, 50);
  }

  handleSuccess() {
    // TODO: factor this out out somewhere
    this.setState({ successfulSave: true });
    setTimeout(() => this.setState({ successfulSave: false }), 1000);
  }

  renderPhotoSection() {
    const { page, onUpload, filePreview, headerImgUrl } = this.props;
    const hfilePreview = filePreview.cloudfront_url ? filePreview.cloudfront_url : headerImgUrl;
    return (
      <div>
        <div className="form-label">
          Photo <small>(max file size 5 MB)</small>
        </div>
        <div className="header-image margin-bottom-2x">
          <div className={`header-image-preview${this.state.hasHeaderImage ? ' has-preview' : ''}`}>
            <FileInput
              filePreview={this.state.hasHeaderImage ? hfilePreview : undefined}
              inputId={`header_image_${page.type}`}
              onSelect={onUpload}
            />
          </div>
          <div className={`header-image-upload${this.state.hasHeaderImage ? ' has-preview' : ''}`}>
            <div className="actions text-center">
              {this.state.hasHeaderImage && (
                <ButtonV2 variant="tertiary" onClick={() => this.removePhoto('header_image')}>
                  Delete
                </ButtonV2>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { pristine, valid, page, showMap, togglePoiMap } = this.props;
    const { successfulSave } = this.state;
    const descriptionPlaceholder = getCmsPageDescriptionPlaceholder(page.type);

    return (
      <form onSubmit={this.onSubmit} id="cms-page-form" noValidate>
        <Field id="cms-page-id" name="id" component={RenderHiddenField} />
        <Field id="cms-page-hidden" name="hidden" component={RenderHiddenField} />
        <Field name="header_image_id" component={RenderHiddenField} />
        <Field name="header_image_url" component={RenderHiddenField} />
        <Field
          name="title"
          type="text"
          label="Page Title"
          id="cms-page-title"
          component={RenderField}
          required
          maxChars={40}
          validate={[Validations.REQUIRED, Validations.MAX_LENGTH_40]}
        />
        <Field
          name="description"
          id="cms-page-description"
          label="Description"
          placeholder={descriptionPlaceholder}
          component={RenderTextareaField}
          maxChars={1000}
          rows={4}
          validate={Validations.MAX_LENGTH_1000}
        />
        <RenderSubmitButton
          pristine={pristine}
          disableWhenPristine
          showSuccess={successfulSave}
          valid={valid}
          className="margin-bottom-2x"
        />
        {this.renderPhotoSection()}
        {page && page.type === 'POI' && (
          <div className="poi-options form-group">
            <span className="toggle-description form-label">Map of Things To Do</span>
            <Toggle
              checked={showMap}
              onChange={() => togglePoiMap(!showMap)}
              icons={{
                checked: <span className="toggle-label checked">Visible</span>,
                unchecked: <span className="toggle-label unchecked">Hidden</span>,
              }}
            />
          </div>
        )}
      </form>
    );
  }
}

CmsPageForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  onUpload: PropTypes.func,
  onUploadSuccess: PropTypes.func,
  filePreview: PropTypes.shape({
    uuid: PropTypes.string,
  }),
  headerImgUrl: PropTypes.string,
  deletePhoto: PropTypes.func,
  page: PropTypes.shape({}),
  routes: PropTypes.arrayOf(PropTypes.shape({})),
  dirty: PropTypes.bool.isRequired,
  showMap: PropTypes.bool,
  togglePoiMap: PropTypes.func,
  onUpdatePage: PropTypes.func,
  formValues: PropTypes.shape({
    header_image_id: PropTypes.string,
  }),
  router: PropTypes.shape({
    setRouteLeaveHook: PropTypes.func,
  }),
  // onSubmit: PropTypes.func
};

const cmsPageFormWithRouter = withRouter(CmsPageForm);
const CmsPageFormInstance = reduxForm({
  form: 'CmsPageForm',
  enableReinitialize: true,
})(cmsPageFormWithRouter);

const selector = formValueSelector('CmsPageForm');

const mapStateToProps = (state, ownProps) => ({
  initialValues: ownProps.page,
  formValues: selector(state, 'header_image_id', 'header_image_url', 'type'),
  filePreview: state.website.entities.filePreview[`header_image_${ownProps.page.type}`] || {},
  headerImgUrl: ownProps.page.header_image_url,
  showMap: state.wedding.account.enable_poi_maps,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  onSubmit: pageView => dispatch(updatePage(pageView)),
  onUpdatePage: pageView => dispatch(updatePage(pageView)),
  onUpload: file => dispatch(uploadPhoto(file, `header_image_${ownProps.page.type}`)),
  onUploadSuccess: image => {
    dispatch(change('CmsPageForm', 'header_image_id', image.uuid));
    dispatch(change('CmsPageForm', 'header_image_url', image.cloudfront_url));
  },
  deletePhoto: () => {
    dispatch(change('CmsPageForm', 'header_image_id', ''));
    dispatch(change('CmsPageForm', 'header_image_url', ''));
  },
  togglePoiMap: hidden => {
    dispatch(updateShowPoiMap(hidden));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CmsPageFormInstance);



// WEBPACK FOOTER //
// ./src/components/manage/website/CmsPageForm.jsx